import { apiClient } from './api';
import type {
  VocabularyWord,
  AddVocabularyWordPayload,
  AddManualVocabularyWordPayload,
  UpdateVocabularyReviewPayload,
  UpdateVocabularyWordPayload,
  VocabularyStats,
  VocabularyStatsByLanguage,
  ApiResponse,
  GenerateVocabPayload,
  VocabSuggestion,
  SaveVocabPayload,
  LinkExistingVocabularyPayload,
  LinkExistingVocabularyResult,
} from '../types/api.types';

export const vocabularyService = {
  // Add a new word to vocabulary
  addWord: async (payload: AddVocabularyWordPayload): Promise<ApiResponse> => {
    const { data } = await apiClient.post<ApiResponse>('/vocabulary/add', payload);
    return data;
  },

  // Add a manually created vocabulary word (not from podcast)
  addManualWord: async (payload: AddManualVocabularyWordPayload): Promise<ApiResponse> => {
    const { data } = await apiClient.post<ApiResponse>('/vocabulary/manual', payload);
    return data;
  },

  // Get all vocabulary words (optionally filtered by project and/or language)
  getWords: async (projectId?: number, language?: string): Promise<VocabularyWord[]> => {
    const params: Record<string, string | number> = {};
    if (projectId) params.projectId = projectId;
    if (language) params.language = language;
    
    const { data } = await apiClient.get<VocabularyWord[]>('/vocabulary/words', {
      params: Object.keys(params).length > 0 ? params : undefined,
    });
    return data;
  },

  // Get words due for review (optionally filtered by language)
  getDueWords: async (language?: string): Promise<VocabularyWord[]> => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const params: Record<string, string> = { timezone };
    if (language) params.language = language;
    const { data } = await apiClient.get<VocabularyWord[]>('/vocabulary/due', { params });
    return data;
  },

  // Submit review rating for a word
  reviewWord: async (payload: UpdateVocabularyReviewPayload): Promise<ApiResponse> => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const { data } = await apiClient.post<ApiResponse>('/vocabulary/review', { ...payload, timezone });
    return data;
  },

  // Update word translation
  updateWord: async (wordId: number, payload: UpdateVocabularyWordPayload): Promise<ApiResponse> => {
    const { data } = await apiClient.put<ApiResponse>(`/vocabulary/${wordId}`, payload);
    return data;
  },

  // Delete a word
  deleteWord: async (wordId: number): Promise<ApiResponse> => {
    const { data } = await apiClient.delete<ApiResponse>(`/vocabulary/${wordId}`);
    return data;
  },

  // Get vocabulary statistics (optionally filtered by project and/or language)
  getStats: async (projectId?: number, language?: string): Promise<VocabularyStats> => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const params: Record<string, string | number> = { timezone };
    if (projectId) params.projectId = projectId;
    if (language) params.language = language;
    
    const { data } = await apiClient.get<VocabularyStats | VocabularyStatsByLanguage>('/vocabulary/stats', {
      params,
    });
    
    // If no language filter, backend returns per-language breakdown
    // Aggregate it into a single stats object
    if ('languages' in data) {
      const aggregated: VocabularyStats = {
        totalWords: 0,
        dueForReview: 0,
        masteredWords: 0,
        learningWords: 0,
        newWords: 0,
      };
      
      data.languages.forEach(lang => {
        aggregated.totalWords += lang.totalWords;
        aggregated.dueForReview += lang.dueForReview;
        aggregated.masteredWords += lang.masteredWords;
        aggregated.learningWords += lang.learningWords;
        aggregated.newWords += lang.newWords;
      });
      
      return aggregated;
    }
    
    return data;
  },

  // Get vocabulary words for a specific segment
  getBySegment: async (segmentId: number): Promise<VocabularyWord[]> => {
    const { data } = await apiClient.get<VocabularyWord[]>(`/vocabulary/segment/${segmentId}`);
    return data;
  },

  // Scan a segment's transcript for words already in the user's vocabulary
  // (same source language) and link any unlinked matches to the segment.
  linkExistingToSegment: async (
    payload: LinkExistingVocabularyPayload,
  ): Promise<LinkExistingVocabularyResult> => {
    const { data } = await apiClient.post<LinkExistingVocabularyResult>(
      '/vocabulary/link-existing',
      payload,
    );
    return data;
  },

  // Refresh word context from current transcript
  refreshContext: async (wordId: number): Promise<ApiResponse> => {
    const { data } = await apiClient.post<ApiResponse>(`/vocabulary/${wordId}/refresh-context`);
    return data;
  },

  // One-time backfill: resolve lexical_word_id for all existing words that have none.
  // Safe to call multiple times — only touches rows where the column is still NULL.
  backfillLexical: async (): Promise<{ updated: number; skipped: number; message: string }> => {
    const { data } = await apiClient.post('/vocabulary/backfill-lexical');
    return data;
  },

  // Generate AI vocabulary suggestions for an episode (Phase 3)
  generateVocab: async (payload: GenerateVocabPayload): Promise<VocabSuggestion[]> => {
    const { data } = await apiClient.post<VocabSuggestion[]>('/ai/vocab/generate', payload);
    return data;
  },

  // Save accepted AI vocabulary cards to the user's collection (Phase 3)
  saveVocabCards: async (payload: SaveVocabPayload): Promise<{ status: string }> => {
    const { data } = await apiClient.post<{ status: string }>('/ai/vocab/save', payload);
    return data;
  },
};
